import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Handle,
  Position,
  useReactFlow,
  useNodeId,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import {
  Button,
  Card,
  CardBody,
  Input,
  InputGroup,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "reactstrap";
import { toast } from "react-toastify";
import Request from "../../../../helpers/request";
import * as url from "../../../../helpers/url_helper";
import AirHomeLoader from "../../../../components/Common/AirHomeLoader";
const appIcon = require("../../../../assets/images/AirHomeNotification.png");

export type TemplateNodeData = {
  templateId: string;
  templateName?: string;
  templateHeading?: string;
  templateContents?: string;
  templateImage?: string | null;
  /** Passed from canvas (no context) */
  templates?: { value: string; label: string }[];
};

export type TemplateNodeType = Node<TemplateNodeData, "template">;

function TemplateNode(props: NodeProps<TemplateNodeType>) {
  const { data } = props;
  const nodeKey = props.id;
  const { setNodes, setEdges, getNodes } = useReactFlow();
  const contextTemplates = data.templates ?? [];
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [templateRows, setTemplateRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchedDetailsRef = useRef(false);
  const detailsFetchInFlightRef = useRef(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Template ID copied");
    } catch (err) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Template ID copied");
      } catch {
        toast.error("Failed to copy Template ID");
      }
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPage(1);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText]);

  // Reset "fetched once" flags when template changes (edit flow)
  useEffect(() => {
    fetchedDetailsRef.current = false;
    detailsFetchInFlightRef.current = false;
  }, [data.templateId]);

  useEffect(() => {
    if (!offcanvasOpen) return;

    let cancelled = false;
    setLoading(true);

    const fetchTemplates = async () => {
      try {
        const response = await Request.post(
          `${url.BASE_URL}${url.GET_NOTIFICATION_TEMPLATE}`,
          {
            page,
            limit: perPage,
            isForERP: false,
            search: debouncedSearch || "",
          },
        );

        if (cancelled) return;
        const dataBlock = response?.data?.data || {};
        const list = dataBlock?.templates || [];
        // Store templates as-is (full API objects)
        setTemplateRows(Array.isArray(list) ? list : []);
        setTotalRows(
          dataBlock?.totalRecords ||
            response?.data?.recordsTotal ||
            response?.data?.totalRecords ||
            (Array.isArray(list) ? list.length : 0) ||
            0,
        );
      } catch (err: any) {
        if (cancelled) return;
        console.error("Error fetching templates:", err);
        toast.error("Failed to load templates");
        setTemplateRows([]);
        setTotalRows(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTemplates();
    return () => {
      cancelled = true;
    };
  }, [offcanvasOpen, debouncedSearch, page, perPage]);

  // Edit-flow: if templateId exists but preview fields are missing, fetch once
  // so the node shows the proper preview card without re-select.
  const hasPreviewFields =
    Boolean((data.templateHeading ?? "").trim()) ||
    Boolean((data.templateContents ?? "").trim()) ||
    Boolean((data.templateImage ?? "")?.toString?.().trim());

  useEffect(() => {
    if (
      offcanvasOpen ||
      !data.templateId ||
      fetchedDetailsRef.current ||
      detailsFetchInFlightRef.current ||
      hasPreviewFields
    )
      return;

    let cancelled = false;
    detailsFetchInFlightRef.current = true;

    const fetchTemplateDetails = async () => {
      try {
        const searchValue =
          (data.templateName ?? "").trim() || (data.templateId ?? "").trim();
        const response = await Request.post(
          `${url.BASE_URL}${url.GET_NOTIFICATION_TEMPLATE}`,
          {
            page: 1,
            limit: 50,
            isForERP: false,
            search: searchValue,
          },
        );
        if (cancelled) return;
        const dataBlock = response?.data?.data || {};
        const list = Array.isArray(dataBlock?.templates)
          ? dataBlock.templates
          : [];
        const wantedId = (data.templateId ?? "").toString();
        let match =
          list.find((t: any) => (t?.id ?? t?._id)?.toString() === wantedId) ||
          null;

        // Fallback: some servers don't search by id; scan pages until found.
        if (!match) {
          const scanLimit = 100;
          let scanPage = 1;
          let scanTotalPages =
            Number(dataBlock?.totalPages) ||
            Math.max(
              1,
              Math.ceil(Number(dataBlock?.totalRecords || 0) / scanLimit) || 1,
            );
          // Cap to avoid long loops
          const maxPagesToScan = Math.min(scanTotalPages, 10);

          while (!match && scanPage <= maxPagesToScan) {
            const scanResp = await Request.post(
              `${url.BASE_URL}${url.GET_NOTIFICATION_TEMPLATE}`,
              {
                page: scanPage,
                limit: scanLimit,
                isForERP: false,
                search: "",
              },
            );
            if (cancelled) return;
            const scanBlock = scanResp?.data?.data || {};
            const scanList = Array.isArray(scanBlock?.templates)
              ? scanBlock.templates
              : [];
            match = scanList.find(
              (t: any) => (t?.id ?? t?._id)?.toString() === wantedId,
            );
            scanTotalPages =
              Number(scanBlock?.totalPages) ||
              Math.max(
                1,
                Math.ceil(Number(scanBlock?.totalRecords || 0) / scanLimit) ||
                  1,
              );
            scanPage += 1;
          }
        }

        if (!match || !nodeKey) return;

        fetchedDetailsRef.current = true;
        setNodes(
          getNodes().map((n) =>
            n.id === nodeKey
              ? {
                  ...n,
                  data: {
                    ...(n.data || {}),
                    templateName:
                      match?.name ||
                      match?.headings ||
                      (n.data as any)?.templateName ||
                      "",
                    templateHeading: match?.headings ?? "",
                    templateContents: match?.contents ?? "",
                    templateImage: match?.global_image ?? null,
                  },
                }
              : n,
          ),
        );
      } catch (err: any) {
        if (!cancelled) console.error("Error fetching template details:", err);
      } finally {
        if (!cancelled) detailsFetchInFlightRef.current = false;
      }
    };

    fetchTemplateDetails();
    return () => {
      cancelled = true;
      detailsFetchInFlightRef.current = false;
    };
  }, [
    offcanvasOpen,
    data.templateId,
    data.templateName,
    hasPreviewFields,
    nodeKey,
    setNodes,
    getNodes,
  ]);

  const filteredTemplateRows = useMemo(() => {
    // API already searches; local filter keeps UI resilient.
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) return templateRows;
    return templateRows.filter((tpl: any) => {
      const name = (tpl?.name ?? "").toString().toLowerCase();
      const headings = (tpl?.headings ?? "").toString().toLowerCase();
      return name.includes(keyword) || headings.includes(keyword);
    });
  }, [templateRows, searchText]);

  const mergedTemplates = useMemo(() => {
    const map = new Map<string, { value: string; label: string }>();
    contextTemplates.forEach((t) => map.set(t.value, t));
    templateRows.forEach((tpl: any) => {
      const rawLabel = tpl?.name || tpl?.headings || tpl?.id || "";
      const templateId = tpl?.id?.toString?.() || tpl?._id?.toString?.() || "";
      if (!templateId) return;
      map.set(templateId, {
        value: templateId,
        label: typeof rawLabel === "string" ? rawLabel : String(rawLabel),
      });
    });
    return Array.from(map.values());
  }, [contextTemplates, templateRows]);

  const selectTemplate = (tplOrId: any) => {
    const tpl =
      typeof tplOrId === "string"
        ? templateRows.find((t: any) => (t?.id ?? t?._id) === tplOrId)
        : tplOrId;
    const value =
      typeof tplOrId === "string"
        ? tplOrId
        : tpl?.id?.toString?.() || tpl?._id?.toString?.() || "";
    if (!value) return;
    const option = mergedTemplates.find((t) => t.value === value);
    const rawLabel = tpl?.name || tpl?.headings || option?.label || value || "";
    const label =
      typeof rawLabel === "string" ? rawLabel : String(rawLabel ?? "");
    setNodes(
      getNodes().map((n) =>
        n.id === nodeKey
          ? {
              ...n,
              data: {
                ...n.data,
                templateId: value,
                templateName: label,
                templateHeading: tpl?.headings ?? "",
                templateContents: tpl?.contents ?? "",
                templateImage: tpl?.global_image ?? null,
              },
            }
          : n,
      ),
    );
    setSearchText("");
    setOffcanvasOpen(false);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const id = nodeKey ?? "";
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  };

  const closeOffcanvas = () => {
    setSearchText("");
    setDebouncedSearch("");
    setPage(1);
    setOffcanvasOpen(false);
  };

  const selectedName = data.templateId
    ? data.templateName ||
      mergedTemplates.find((t) => t.value === data.templateId)?.label ||
      "Selected template"
    : null;
  const hasSelection = !!data.templateId;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
  const startRow = totalRows === 0 ? 0 : (page - 1) * perPage + 1;
  const endRow = Math.min(page * perPage, totalRows);
  const hasPreview = Boolean(
    (data.templateHeading ?? "").trim() ||
      (data.templateContents ?? "").trim() ||
      ((data.templateImage ?? "") as any)?.toString?.().trim(),
  );

  return (
    <>
      <div
        style={{
          minWidth: hasSelection ? 220 : 180,
          maxWidth: hasSelection ? 220 : undefined,
          background: "#eff6ff",
          border: "1px solid #93c5fd",
          borderRadius: "6px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          id="template-in"
          style={{ top: "50%" }}
        />
        <button
          type="button"
          onClick={handleRemove}
          className="nodrag nopan"
          aria-label="Remove node"
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 16,
            height: 16,
            padding: 0,
            border: "none",
            borderRadius: 3,
            background: "rgba(0,0,0,0.06)",
            color: "#374151",
            cursor: "pointer",
            fontSize: 12,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <box-icon name="trash" size="12px" color="#454545"></box-icon>
        </button>
        <div
          style={{
            fontWeight: 600,
            fontSize: 10,
            padding: "4px",
            color: "#1e40af",
            cursor: "grab",
            textAlign: "center",
            borderBottom: hasSelection ? "1px solid #bfdbfe" : undefined,
          }}
        >
          Template
        </div>
        <div
          className="nodrag"
          onClick={() => setOffcanvasOpen(true)}
          style={{ cursor: "pointer" }}
        >
          {hasSelection ? (
            <Card className="border-0 shadow-none mb-0 rounded-0 bg-transparent">
              <CardBody className="p-2">
                {hasPreview ? (
                  <div
                    style={{
                      marginBottom: 2,
                      padding: 8,
                      background: "#ffffff",
                      borderRadius: 10,
                      border: "1px solid #dbeafe",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 6,
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            width: "22px",
                            height: "22px",
                            borderRadius: "20%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={appIcon.default || appIcon}
                            alt="App Icon"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#111827",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          AirHome
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: 9,
                          color: "#6b7280",
                          flexShrink: 0,
                        }}
                      >
                        now
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginBottom: 2,
                      }}
                    >
                      {(data.templateHeading ?? "").trim() || selectedName}
                    </div>
                    <div
                      style={{
                        fontSize: 9,
                        color: "#374151",
                      }}
                    >
                      {(data.templateContents ?? "").trim()}
                    </div>
                    {data.templateImage ? (
                      <img
                        src={data.templateImage}
                        alt="Template"
                        style={{
                          width: "100%",
                          objectFit: "cover",
                          borderRadius: 10,
                          marginTop: 8,
                          display: "block",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        marginTop: 10,
                        paddingTop: 8,
                        borderTop: "1px solid #e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#0f172a",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={(
                          data.templateName ??
                          selectedName ??
                          ""
                        ).toString()}
                      >
                        {(data.templateName ?? selectedName ?? "").toString()}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div style={{ paddingRight: 18 }}>
                    <span
                      title={selectedName ?? ""}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#1e3a8a",
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedName}
                    </span>
                  </div>
                )}
                <div
                  className="text-center mt-2 pt-1 border-top"
                  style={{ borderColor: "#bfdbfe" }}
                >
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 500,
                      color: "#1d4ed8",
                    }}
                  >
                    Click to change
                  </span>
                </div>
              </CardBody>
            </Card>
          ) : (
            <div className="px-2 py-2">
              <Button
                color="light"
                size="sm"
                outline
                className="w-100 d-flex align-items-center justify-content-center"
                style={{
                  fontSize: 10,
                  minHeight: 26,
                  border: "1px dashed #93c5fd",
                  borderRadius: 6,
                  fontWeight: 500,
                  color: "#1e40af",
                  backgroundColor: "#eff6ff",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOffcanvasOpen(true);
                }}
              >
                Select template
              </Button>
            </div>
          )}
        </div>
      </div>
      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        style={{ width: "90%" }}
        className="nodrag nopan"
      >
        <OffcanvasHeader
          toggle={closeOffcanvas}
          className="d-flex align-items-center justify-content-between  "
        >
          <h5
            className="modal-title"
            style={{ textAlign: "center", fontWeight: 800 }}
          >
            Select template
          </h5>
        </OffcanvasHeader>
        <OffcanvasBody
          className="d-flex flex-column p-0"
          style={{ overflow: "hidden", backgroundColor: "#f7f7fa" }}
        >
          <div className="px-3 py-2 flex-shrink-0">
            <InputGroup size="sm">
              <Input
                type="search"
                placeholder="Search by template name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="border"
                style={{ fontSize: 13 }}
              />
            </InputGroup>
          </div>
          <div className="d-flex flex-row flex-wrap gap-2 px-3 pb-3 flex-grow-1 overflow-auto align-content-start">
            {loading && <AirHomeLoader />}
            {!loading && filteredTemplateRows.length === 0 ? (
              <p className="text-muted small mb-0">
                {templateRows.length === 0
                  ? "No templates available."
                  : "No templates match your search."}
              </p>
            ) : (
              !loading &&
              filteredTemplateRows.map((t: any) => (
                <Card
                  key={(t?.id ?? t?._id ?? "").toString()}
                  tag="button"
                  type="button"
                  className="text-start border shadow-sm flex-shrink-0"
                  style={{
                    cursor: "pointer",
                    transition: "box-shadow 0.15s ease",
                    minWidth: 330,
                    maxWidth: 330,
                    minHeight: 300,
                    padding: 0,
                    margin: 0,
                  }}
                  onClick={() => selectTemplate(t)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <CardBody
                    className="p-3"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#1e293b",
                        display: "block",
                      }}
                    >
                      {(t?.headings ?? "").toString() || "-"}
                    </span>
                    <span
                      className="text-muted"
                      style={{
                        fontSize: 12,
                        display: "block",
                        marginTop: 4,
                      }}
                    >
                      {(t?.contents ?? "").toString()}
                    </span>
                    {t?.global_image ? (
                      <img
                        src={t.global_image}
                        alt="Template"
                        style={{
                          width: "100%",
                          height: 130,
                          objectFit: "cover",
                          borderRadius: 10,
                          marginTop: 10,
                          display: "block",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                    <div
                      className="d-flex  align-items-center justify-content-between"
                      style={{
                        gap: 8,
                        marginTop: "auto",
                        paddingTop: 8,
                        borderTop: "1px solid #e5e7eb",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#0f172a",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={(t?.name ?? "").toString()}
                      >
                        {(t?.name ?? "").toString() || "Template"}
                      </span>
                      <Button
                        type="button"
                        color="light"
                        size="sm"
                        className="d-flex align-items-center justify-content-center"
                        style={{ fontSize: 11, padding: "4px" }}
                        title={t?.id}
                        aria-label={t?.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const id = (t?.id ?? t?._id ?? "").toString();
                          if (!id) {
                            toast.error("Template ID not found");
                            return;
                          }
                          copyToClipboard(id);
                        }}
                      >
                        <box-icon
                          name="copy-alt"
                          size="16px"
                          color="#6d6d6eff"
                        ></box-icon>
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))
            )}
          </div>
          {!loading && totalRows > 0 && (
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 border-top flex-shrink-0"
              style={{ fontSize: 12, backgroundColor: "#ffffff" }}
            >
              <span>
                Showing {startRow}-{endRow} of {totalRows}
              </span>
              <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", fontSize: 12 }}
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} per page
                    </option>
                  ))}
                </select>
                <Button
                  color="light"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <Button
                  color="light"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
}

export default TemplateNode;
